import React, { useState } from 'react';
import axios from 'axios';
import { PrimaryButton } from '../../shared';
import MessageFeedback from '../components/MessageFeedback';
import PlayerPropsCard from '../components/PlayerPropsCard';
import './AskAiPageV2.css';

const API_ENDPOINT = 'https://bvjnueuoz7cb3kwtbabltt6icy0tutqe.lambda-url.us-east-1.on.aws';

// Simple markdown to HTML converter
const formatMessage = (text) => {
  if (!text) return '';
  return text
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    .replace(/\n/g, '<br/>');
};

const SUGGESTED_PROMPTS = [
  "Top 3 conservative picks tonight",
  "Any line movement steam today?",
  "Best value spreads in NBA",
  "Explain why this pick is strong",
  "Find a safer alternative to this pick"
];

const AskAiPageV2 = ({ user }) => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `betpilot-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const sendMessage = async (text = query) => {
    if (!text.trim()) return;
    
    const userMsgId = `msg-${Date.now()}-user`;
    setMessages(prev => [...prev, { id: userMsgId, role: 'user', content: text }]);
    setQuery('');
    setLoading(true);

    try {
      const token = localStorage.getItem('idToken');
      const res = await axios.post(API_ENDPOINT, { 
        prompt: text,
        session_id: sessionId,
        user_id: user?.email || 'anonymous'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const assistantMsgId = `msg-${Date.now()}-assistant`;
      setMessages(prev => [...prev, { 
        id: assistantMsgId,
        role: 'assistant', 
        content: res.data.message || res.data.response || 'No response',
        structuredData: res.data.structured_data,
        audit: res.data.audit,
        userQuery: text
      }]);
    } catch (e) {
      setMessages(prev => [...prev, { id: `msg-${Date.now()}-error`, role: 'error', content: `Error: ${e.message}` }]);
    }
    setLoading(false);
  };

  return (
    <div className="ask-ai-v2">
      <div className="chat-container-v2">
        <div className="messages-v2">
          {messages.length === 0 && (
            <div className="welcome-state">
              <h3>💬 Ask the AI</h3>
              <p>Get personalized betting insights and analysis</p>
              <div className="suggested-prompts">
                {SUGGESTED_PROMPTS.map((prompt, i) => (
                  <button key={i} className="prompt-chip" onClick={() => sendMessage(prompt)}>
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {messages.map((msg, i) => (
            <div key={msg.id} className={`message-v2 ${msg.role}`}>
              {msg.structuredData?.type === 'player_props_card' ? (
                <PlayerPropsCard data={msg.structuredData} />
              ) : (
                <div className="message-content" dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />
              )}
              {msg.role === 'assistant' && (
                <MessageFeedback
                  messageId={msg.id}
                  sessionId={sessionId}
                  userId={user?.email || 'anonymous'}
                  messageData={{
                    user_query: msg.userQuery,
                    tools_called: msg.audit?.tools_called || [],
                    evidence_count: msg.audit?.evidence_count || 0,
                    confidence: msg.audit?.confidence?.level
                  }}
                />
              )}
            </div>
          ))}
          
          {loading && <div className="message-v2 assistant loading">Analyzing...</div>}
        </div>

        <div className="input-row-v2">
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && sendMessage()}
            placeholder="Ask about betting opportunities..."
            disabled={loading}
          />
          <PrimaryButton onClick={() => sendMessage()} disabled={loading || !query.trim()}>
            Send
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default AskAiPageV2;
